import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useUserAuth } from "../../hooks/useUserAuth";
import {
  createRegisterSchema,
  RegisterFormData,
} from "../../helpers/userValidation";
import PasswordRequirements from "../common/PasswordRequirements";

const RegisterForm: React.FC = () => {
  const { t } = useTranslation();
  const { handleAuth, isLoading } = useUserAuth();
  const [isSuccess, setIsSuccess] = useState(false);
  const membershipAgreementUrl =
    window.commonVariables?.membershipAgreementUrl || "#";

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(createRegisterSchema(t)),
  });

  // Watch the password field to update requirements real-time
  const passwordValue = watch("password");

  // --- TIMESTAMP LOGIC ---
  useEffect(() => {
    // Register the field with react-hook-form
    register("form_load_timestamp");
    // Set its value to the current time in seconds
    setValue("form_load_timestamp", Math.floor(Date.now() / 1000));
  }, [register, setValue]);
  // --- END TIMESTAMP ---

  const onSubmit = async (data: RegisterFormData) => {
    const response = await handleAuth({
      type: "register",
      ...data,
    });

    if (response.success) {
      setIsSuccess(true);
    }
  };

  if (isSuccess) {
    return (
      <div
        className="auth-success-message"
        style={{ textAlign: "center", padding: "2rem 1rem" }}
      >
        <h3 style={{ color: "var(--color-primary)", marginBottom: "1rem" }}>
          {t("user.auth.pendingSuccessTitle", "Başvurunuz Alındı!")}
        </h3>
        <p
          style={{
            color: "var(--color-text-muted)",
            marginBottom: "1.5rem",
            lineHeight: "1.6",
          }}
        >
          {t(
            "user.auth.pendingSuccessMessage",
            "Üyelik başvurunuz başarıyla alındı. Hesabınız yöneticilerimiz tarafından incelendikten sonra onaylanacak ve tarafınıza bilgilendirme e-postası gönderilecektir.",
          )}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="auth-form" noValidate>
      {/* --- HONEYPOT FIELD --- */}
      <div className="honeypot-field" aria-hidden="true">
        <label htmlFor="user_nickname_register">Nickname</label>
        <input
          id="user_nickname_register"
          type="text"
          {...register("nickname")}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>
      {/* --- END HONEYPOT --- */}

      <div className="form-field">
        <label htmlFor="reg-email">{t("user.auth.emailLabel")}</label>
        <input
          id="reg-email"
          type="email"
          {...register("email")}
          autoComplete="email"
          className={errors.email ? "has-error" : ""}
        />
        {errors.email && (
          <p className="form-field__error">{errors.email.message}</p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor="reg-username">
          {t("user.auth.usernameOptionalLabel")}
        </label>
        <input
          id="reg-username"
          type="text"
          {...register("username")}
          autoComplete="username"
          className={errors.username ? "has-error" : ""}
        />
        {errors.username && (
          <p className="form-field__error">{errors.username.message}</p>
        )}
      </div>

      <div className="form-field">
        <label htmlFor="reg-password">
          {t("user.auth.passwordRequiredLabel")}
        </label>
        <input
          id="reg-password"
          type="password"
          {...register("password")}
          autoComplete="new-password"
          className={errors.password ? "has-error" : ""}
        />
        <PasswordRequirements
          password={passwordValue}
          isVisible={!!passwordValue}
        />
        {errors.password && (
          <p className="form-field__error">{errors.password.message}</p>
        )}
      </div>

      {/* --- MAILING CONSENT --- */}
      <div className="form-field form-field--checkbox">
        <label htmlFor="reg-mailing-consent">
          <input
            id="reg-mailing-consent"
            type="checkbox"
            {...register("marketing_optin")}
          />
          <span className="checkbox-text">
            {t(
              "mailingConsent.checkboxLabel",
              "I want to receive news and exclusive offers via email.",
            )}
          </span>
        </label>
      </div>

      {/* --- MEMBERSHIP AGREEMENT --- */}
      <div className="form-field form-field--checkbox">
        <label htmlFor="reg-agreement">
          <input
            id="reg-agreement"
            type="checkbox"
            {...register("agreement")}
            className={errors.agreement ? "has-error" : ""}
          />
          <span className="checkbox-text">
            <a
              href={membershipAgreementUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                textDecoration: "underline",
                color: "var(--color-primary)",
              }}
            >
              {t(
                "user.auth.agreementLinkText",
                "I have read and agree to the Membership Agreement.",
              )}
            </a>
          </span>
        </label>
        {errors.agreement && (
          <p className="form-field__error">{errors.agreement.message}</p>
        )}
      </div>

      <button type="submit" disabled={isLoading}>
        {isLoading
          ? t("user.auth.registerButtonLoading")
          : t("user.auth.registerButton")}
      </button>
    </form>
  );
};

export default RegisterForm;
